import * as React from "react"
import { Listbox, Transition } from "@headlessui/react"
import { ChevronUpDownIcon, CheckIcon } from "@heroicons/react/20/solid"

import { cn } from "@/utils"

export interface SelectOption {
  label: React.ReactNode
  value: string | number
  disabled?: boolean
}

export interface SelectProps {
  /** Currently selected value */
  value?: string | number
  /** Called when selection changes */
  onChange?: (value: string | number) => void
  /** Options list */
  options: SelectOption[]
  /** Placeholder text */
  placeholder?: string
  /** Disabled state */
  disabled?: boolean
  /** Additional className */
  className?: string
  /** Size variant */
  size?: "sm" | "default" | "lg"
}

function Select({
  value,
  onChange,
  options,
  placeholder = "Select...",
  disabled,
  className,
  size = "default",
}: SelectProps) {
  const selected = options.find((o) => o.value === value)

  const sizeClasses = {
    sm: "h-8 text-xs px-2",
    default: "h-10 text-sm px-3",
    lg: "h-12 text-base px-4",
  }

  return (
    <Listbox value={value} onChange={onChange} disabled={disabled}>
      <div className={cn("relative", className)}>
        <Listbox.Button
          className={cn(
            "relative w-full cursor-pointer rounded-md border border-input bg-background py-2 text-left",
            "focus:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2",
            "disabled:cursor-not-allowed disabled:opacity-50",
            sizeClasses[size]
          )}
        >
          <span className={cn("block truncate", !selected && "text-muted-foreground")}>
            {selected?.label ?? placeholder}
          </span>
          <span className="pointer-events-none absolute inset-y-0 right-0 flex items-center pr-2">
            <ChevronUpDownIcon className="h-4 w-4 text-muted-foreground" />
          </span>
        </Listbox.Button>
        <Transition
          as={React.Fragment}
          leave="transition ease-in duration-100"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <Listbox.Options className="absolute z-50 mt-1 max-h-60 w-full overflow-auto rounded-md border border-border bg-popover py-1 text-sm shadow-lg focus:outline-none">
            {options.map((option) => (
              <Listbox.Option
                key={option.value}
                value={option.value}
                disabled={option.disabled}
                className={({ active }) =>
                  cn(
                    "relative cursor-pointer select-none py-2 pl-10 pr-4",
                    active && "bg-accent text-accent-foreground",
                    option.disabled && "cursor-not-allowed opacity-50"
                  )
                }
              >
                {({ selected: isSelected }) => (
                  <>
                    <span className={cn("block truncate", isSelected && "font-medium")}>
                      {option.label}
                    </span>
                    {isSelected && (
                      <span className="absolute inset-y-0 left-0 flex items-center pl-3 text-primary">
                        <CheckIcon className="h-4 w-4" />
                      </span>
                    )}
                  </>
                )}
              </Listbox.Option>
            ))}
          </Listbox.Options>
        </Transition>
      </div>
    </Listbox>
  )
}

export { Select }
